import { useId, useState, type KeyboardEvent } from "react";
import {
  DEFAULT_TAG_COLOR,
  TAG_COLORS,
  TAG_COLOR_CLASS,
  TAG_COLOR_KEY,
  type TagColor,
  type TagView,
} from "../app/tags";
import { PlusIcon } from "./icons";
import { useT } from "../i18n/index.js";
import { cn } from "../lib/cn";

/**
 * Теги — putting tags on one thing (SPEC §4.6).
 *
 * Closed, it is what the card shows: the tags, read-only. Open, the same chips
 * become "tap to remove", the space's other tags are offered, and whatever is
 * typed becomes a new tag when nothing matches it — the three things a person
 * wants from a tag control, without a modal.
 *
 * Presentational on purpose: it takes tags and callbacks, so the screen holding
 * it keeps one `useTags` rather than one per row.
 */

export interface TagPickerProps {
  /** The tags on this thing. */
  selected: TagView[];
  /** Every tag in the space. The ones already on it are filtered out here. */
  available: TagView[];
  onLink(tagId: string): void;
  onUnlink(tagId: string): void;
  onCreate(name: string, color: TagColor): void;
  /** What is being tagged, so a screen reader hears «Теги: плед на диван». */
  label: string;
  className?: string;
}

const fold = (value: string) => value.trim().toLocaleLowerCase("ru");

export function TagPicker({
  selected,
  available,
  onLink,
  onUnlink,
  onCreate,
  label,
  className,
}: TagPickerProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [color, setColor] = useState<TagColor>(DEFAULT_TAG_COLOR);
  const inputId = useId();

  const on = new Set(selected.map((tag) => tag.id));
  const typed = fold(query);

  const offered = available
    .filter((tag) => !on.has(tag.id))
    .filter((tag) => typed === "" || fold(tag.name).includes(typed));

  // An exact name, whether or not it is already on this thing: typing one that
  // exists must never mint a second tag with the same name.
  const exact = available.find((tag) => fold(tag.name) === typed);
  const canCreate = typed !== "" && exact === undefined;

  function close() {
    setOpen(false);
    setQuery("");
    setColor(DEFAULT_TAG_COLOR);
  }

  /** Enter: an exact name links that tag, anything else makes a new one. */
  function commit() {
    if (typed === "") return;

    if (exact) {
      if (!on.has(exact.id)) onLink(exact.id);
    } else {
      onCreate(query.trim(), color);
    }

    setQuery("");
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      // First press clears what was typed, second closes — the same shape as
      // every search field, so nothing is lost by a stray key.
      if (query) setQuery("");
      else close();
    }
  }

  return (
    <div
      role="group"
      aria-label={t("tags.group", { label })}
      className={cn("flex flex-col gap-2", className)}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.map((tag) =>
          open ? (
            // Open, the whole chip is the target — one 44px hit area rather
            // than a chip with a cross too small for a thumb beside it.
            <button
              key={tag.id}
              type="button"
              onClick={() => onUnlink(tag.id)}
              aria-label={t("tags.remove", { name: tag.name })}
              className={cn(
                "inline-flex min-h-[var(--touch)] items-center gap-1.5 rounded-pill px-3.5",
                "text-[0.8125rem] transition-opacity hover:opacity-80",
                TAG_COLOR_CLASS[tag.color],
              )}
            >
              <span className="break-words">{tag.name}</span>
              <span aria-hidden="true" className="text-[1rem] leading-none opacity-70">
                ×
              </span>
            </button>
          ) : (
            <TagChip key={tag.id} tag={tag} />
          ),
        )}

        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t("tags.edit", { label })}
            className={cn(
              "inline-flex min-h-[var(--touch)] items-center gap-1 rounded-card px-2",
              "text-[0.8125rem] text-muted transition-colors hover:bg-surface-alt",
            )}
          >
            <PlusIcon className="size-3.5" aria-hidden="true" />
            {selected.length === 0 ? t("tags.label") : null}
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="flex flex-col gap-2 rounded-row bg-surface-alt/60 p-2.5">
          <label htmlFor={inputId} className="sr-only">
            {t("tags.name")}
          </label>
          <input
            id={inputId}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            autoFocus
            enterKeyHint="done"
            placeholder={t("tags.find")}
            className={cn(
              "min-h-[var(--touch)] w-full rounded-row bg-surface-alt px-4",
              "outline-none placeholder:text-muted",
            )}
          />

          {offered.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {offered.map((tag) => (
                <li key={tag.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onLink(tag.id);
                      setQuery("");
                    }}
                    aria-label={t("tags.add", { name: tag.name })}
                    className={cn(
                      "inline-flex min-h-[var(--touch)] items-center rounded-pill px-3.5",
                      "text-[0.8125rem] transition-opacity hover:opacity-80",
                      TAG_COLOR_CLASS[tag.color],
                    )}
                  >
                    {tag.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {canCreate ? (
            <>
              {/* The colour is picked before the tag exists, because picking it
                  afterwards means finding the tag again somewhere else. */}
              <div
                role="group"
                aria-label={t("tags.newColor")}
                className="flex flex-wrap gap-1.5"
              >
                {TAG_COLORS.map((token) => (
                  <button
                    key={token}
                    type="button"
                    onClick={() => setColor(token)}
                    aria-pressed={color === token}
                    aria-label={t("tags.color", { color: t(TAG_COLOR_KEY[token]) })}
                    className={cn(
                      "grid size-[var(--touch)] place-items-center rounded-card transition-opacity",
                      TAG_COLOR_CLASS[token],
                      color === token ? "ring-2 ring-accent" : "hover:opacity-80",
                    )}
                  >
                    <span aria-hidden="true" className="text-[0.875rem]">
                      {color === token ? "✓" : ""}
                    </span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={commit}
                className={cn(
                  "inline-flex min-h-[var(--touch)] items-center justify-start gap-2",
                  "rounded-card px-2 text-[0.875rem] text-accent-ink",
                  "transition-colors hover:bg-surface-alt",
                )}
              >
                <PlusIcon className="size-4" aria-hidden="true" />
                {t("tags.create", { name: query.trim() })}
              </button>
            </>
          ) : null}

          {offered.length === 0 && !canCreate ? (
            <p className="px-1 py-1 text-[0.8125rem] text-muted">
              {exact
                ? t("tags.already", { name: exact.name })
                : available.length === 0
                  ? t("tags.none")
                  : t("tags.all")}
            </p>
          ) : null}

          <button
            type="button"
            onClick={close}
            className={cn(
              "inline-flex min-h-[var(--touch)] items-center justify-start rounded-card px-2",
              "text-[0.8125rem] text-muted transition-colors hover:bg-surface-alt",
            )}
          >
            {t("tags.done")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** A tag as it reads on a card: no target, no affordance, just the name. */
export function TagChip({ tag, className }: { tag: TagView; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-pill px-2.5 py-0.5 text-[0.75rem]",
        TAG_COLOR_CLASS[tag.color],
        className,
      )}
    >
      {tag.name}
    </span>
  );
}
